import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { AISidebar, AIRecommendationPanel } from '../components/ai';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
import { RadioGroup } from '../components/ui/RadioGroup';
import { Button } from '../components/ui/Button';
import { Upload, ArrowLeft, Gem } from 'lucide-react';
// Diamond to Euro conversion rate
const DIAMOND_TO_EURO = 0.1;
const RewardCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    show: boolean;
  }>({
    message: '',
    show: false
  });
  // Form State
  const [formData, setFormData] = useState({
    type: 'multi-use',
    name: '',
    code: '',
    description: '',
    terms: '',
    cost: '',
    totalUsesLimit: 'unlimited',
    totalUsesQuantity: '',
    perUserLimit: 'unlimited',
    perUserQuantity: '',
    startDate: '',
    endDate: '',
    locations: [] as string[]
  });
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Calculate Euro value from Diamond cost
  const euroValue = useMemo(() => {
    const diamonds = parseFloat(formData.cost) || 0;
    return (diamonds * DIAMOND_TO_EURO).toFixed(2);
  }, [formData.cost]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/rewards');
    }, 1500);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-1513">
      <div className="w-20 flex-shrink-0" data-id="element-1514">
        <Sidebar data-id="element-1515" />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1516">
        <Topbar data-id="element-1517" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-1518">
          <div className="mb-6" data-id="element-1519">
            <button onClick={() => navigate('/rewards')} className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-4" data-id="element-1520">
              <ArrowLeft className="h-4 w-4 mr-1" data-id="element-1521" />
              Torna ai premi
            </button>
            <div className="flex items-center justify-between" data-id="element-1522">
              <div data-id="element-1523">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-1524">
                  Crea premio
                </h1>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1525">
                  Configura un'offerta riscattabile con diamond.
                </p>
              </div>
              <div style={{
              background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
              padding: '1px',
              borderRadius: '100px'
            }} data-id="element-1526">
                <button onClick={() => setIsAISidebarOpen(true)} className="text-black transition-colors flex items-center gap-1.5 bg-white px-4 py-2 hover:bg-gray-50" style={{
                borderRadius: '100px'
              }} title="AI Insights" data-id="element-1527">
                  <span className="text-sm" data-id="element-1528">Suggerimenti AI</span>
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1529">
            {/* 1. Tipo Premio */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1530">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1531">
                Tipo di Premio
              </h3>
              <RadioGroup name="type" value={formData.type} onChange={val => handleInputChange('type', val)} options={[{
              value: 'multi-use',
              label: 'Codice sconto multi-uso',
              description: 'Un unico codice utilizzabile da più utenti (es. SUMMER2025)'
            }, {
              value: 'one-time',
              label: 'Offerta one-time',
              description: 'Codici univoci, ognuno valido una sola volta'
            }, {
              value: 'other',
              label: 'Altro',
              description: 'Premi fisici, eventi o altre tipologie senza codice'
            }]} helperText="Scegli il metodo di riscatto più adatto." data-id="element-1532" />
            </div>

            {/* 2. Dettagli */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1533">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1534">
                Dettagli
              </h3>
              <div className="space-y-4" data-id="element-1535">
                <Input label="Nome premio" placeholder="Es. Buono Amazon €50" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} required data-id="element-1536" />
                <Input label="Codice premio" placeholder="Es. SUMMER2025" value={formData.code} onChange={e => handleInputChange('code', e.target.value)} required helperText="Codice univoco per identificare il premio" data-id="element-1537" />
                <Textarea label="Descrizione breve" placeholder="Descrivi cosa riceverà l'utente..." value={formData.description} onChange={e => handleInputChange('description', e.target.value)} rows={3} data-id="element-1538" />
                <Textarea label="Termini e Condizioni" placeholder="Inserisci eventuali limitazioni o istruzioni per l'uso..." value={formData.terms} onChange={e => handleInputChange('terms', e.target.value)} rows={3} data-id="element-1539" />
              </div>
            </div>

            {/* 3. Costo in Diamond */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1540">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1541">
                Costo in Diamond
              </h3>
              <div className="max-w-md" data-id="element-1542">
                <Input label="Costo in Diamond" type="number" placeholder="0" value={formData.cost} onChange={e => handleInputChange('cost', e.target.value)} required min="0" max="999" data-id="element-1543" />
                {formData.cost && parseFloat(formData.cost) > 0 && <div className="mt-2 flex items-center gap-2 text-sm" data-id="element-1544">
                    <Gem className="h-4 w-4 text-gray-500" data-id="element-1545" />
                    <span className="text-gray-600" data-id="element-1546">
                      {formData.cost} Diamond ≈{' '}
                      <span className="font-semibold text-gray-900" data-id="element-1547">
                        €{euroValue}
                      </span>
                    </span>
                  </div>}
                <p className="text-xs text-gray-500 mt-2" data-id="element-1548">
                  Massimo 999 Diamond per premio
                </p>
              </div>
            </div>

            {/* 4. Limiti di Utilizzo (ONLY for multi-use) */}
            {formData.type === 'multi-use' && <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1549">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1550">
                  Limiti di Utilizzo
                </h3>

                {/* Total Uses Section */}
                <div className="mb-6" data-id="element-1551">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1552">
                    Utilizzi Totali del Codice
                  </label>
                  <div className="space-y-3" data-id="element-1553">
                    <label className="flex items-start cursor-pointer" data-id="element-1554">
                      <input type="radio" name="totalUsesLimit" value="unlimited" checked={formData.totalUsesLimit === 'unlimited'} onChange={() => handleInputChange('totalUsesLimit', 'unlimited')} className="mt-0.5 text-greenback focus:ring-greenback" data-id="element-1555" />
                      <div className="ml-3" data-id="element-1556">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1557">
                          Illimitati
                        </span>
                        <p className="text-xs text-gray-500" data-id="element-1558">
                          Il codice può essere utilizzato infinite volte
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start cursor-pointer" data-id="element-1559">
                      <input type="radio" name="totalUsesLimit" value="limited" checked={formData.totalUsesLimit === 'limited'} onChange={() => handleInputChange('totalUsesLimit', 'limited')} className="mt-0.5 text-greenback focus:ring-greenback" data-id="element-1560" />
                      <div className="ml-3 flex-1" data-id="element-1561">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1562">
                          Limitati a un numero massimo
                        </span>
                        <p className="text-xs text-gray-500 mb-2" data-id="element-1563">
                          Imposta un limite totale di utilizzi per questo codice
                        </p>
                        {formData.totalUsesLimit === 'limited' && <Input type="number" placeholder="Es. 1000" value={formData.totalUsesQuantity} onChange={e => handleInputChange('totalUsesQuantity', e.target.value)} min="1" helperText="Numero massimo di volte che il codice può essere utilizzato in totale" data-id="element-1564" />}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-6" data-id="element-1565"></div>

                {/* Per User Section */}
                <div data-id="element-1566">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1567">
                    Utilizzi per Singolo Utente
                  </label>
                  <div className="space-y-3" data-id="element-1568">
                    <label className="flex items-start cursor-pointer" data-id="element-1569">
                      <input type="radio" name="perUserLimit" value="unlimited" checked={formData.perUserLimit === 'unlimited'} onChange={() => handleInputChange('perUserLimit', 'unlimited')} className="mt-0.5 text-greenback focus:ring-greenback" data-id="element-1570" />
                      <div className="ml-3" data-id="element-1571">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1572">
                          Illimitati
                        </span>
                        <p className="text-xs text-gray-500" data-id="element-1573">
                          Ogni utente può utilizzare il codice più volte
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start cursor-pointer" data-id="element-1574">
                      <input type="radio" name="perUserLimit" value="once" checked={formData.perUserLimit === 'once'} onChange={() => handleInputChange('perUserLimit', 'once')} className="mt-0.5 text-greenback focus:ring-greenback" data-id="element-1575" />
                      <div className="ml-3" data-id="element-1576">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1577">
                          Una volta per utente
                        </span>
                        <p className="text-xs text-gray-500" data-id="element-1578">
                          Ogni utente può riscattare il premio una sola volta
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start cursor-pointer" data-id="element-1579">
                      <input type="radio" name="perUserLimit" value="limited" checked={formData.perUserLimit === 'limited'} onChange={() => handleInputChange('perUserLimit', 'limited')} className="mt-0.5 text-greenback focus:ring-greenback" data-id="element-1580" />
                      <div className="ml-3 flex-1" data-id="element-1581">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1582">
                          Limitati per utente
                        </span>
                        <p className="text-xs text-gray-500 mb-2" data-id="element-1583">
                          Ogni utente può utilizzare il codice un numero
                          limitato di volte
                        </p>
                        {formData.perUserLimit === 'limited' && <Input type="number" placeholder="Es. 3" value={formData.perUserQuantity} onChange={e => handleInputChange('perUserQuantity', e.target.value)} min="1" helperText="Numero massimo di utilizzi per singolo utente" data-id="element-1584" />}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1585">
                  <p className="text-sm text-blue-900" data-id="element-1586">
                    <strong data-id="element-1587">Esempio:</strong> Se imposti "Utilizzi Totali: 1000"
                    e "Una volta per utente", massimo 1000 utenti diversi
                    potranno riscattare il premio, ognuno una sola volta.
                  </p>
                </div>
              </div>}

            {/* 5. Validità e Sedi */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1588">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1589">
                Validità e Sedi
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1590">
                <Input label="Data inizio (opzionale)" type="date" value={formData.startDate} onChange={e => handleInputChange('startDate', e.target.value)} data-id="element-1591" />
                <Input label="Data fine (opzionale)" type="date" value={formData.endDate} onChange={e => handleInputChange('endDate', e.target.value)} data-id="element-1592" />
              </div>
              <Select label="Sedi applicabili" options={[{
              value: 'all',
              label: 'Tutte le sedi'
            }, {
              value: 'amsterdam',
              label: 'Amsterdam'
            }, {
              value: 'berlin',
              label: 'Berlin'
            }, {
              value: 'barcelona',
              label: 'Barcelona'
            }, {
              value: 'vienna',
              label: 'Vienna'
            }, {
              value: 'paris',
              label: 'Paris'
            }]} value="all" onChange={() => {}} data-id="element-1593" />
            </div>

            {/* Caricamento Codici One-time */}
            {formData.type === 'one-time' && <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1594">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1595">
                  Caricamento Codici
                </h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1596">
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" data-id="element-1597" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-1598">
                    Clicca per caricare il file CSV
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1599">
                    O trascina il file qui
                  </p>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="element-1600">
                  <span data-id="element-1601">Codici rimanenti: 0</span>
                  <span className="text-xs text-gray-400" data-id="element-1602">
                    Nessun file caricato
                  </span>
                </div>
              </div>}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 pb-10" data-id="element-1603">
              <Button type="button" variant="ghost" onClick={() => navigate('/rewards')} data-id="element-1604">
                Annulla
              </Button>
              <Button type="button" variant="outline" onClick={() => {}} data-id="element-1605">
                Salva bozza
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading} data-id="element-1606">
                Pubblica premio
              </Button>
            </div>
          </form>
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Insights per la Creazione Premio" data-id="element-1607">
        <AIRecommendationPanel recommendations={[{
        id: '1',
        title: 'Preferenze utenti: sconti concentrati',
        type: 'Trend Comportamentale',
        diamondRange: 'Ultimi 30 giorni',
        reason: 'Gli utenti Greenback preferiscono sconti più alti su un numero minore di prodotti rispetto a sconti minori su linee più ampie. I premi con sconto 20%+ su 1-3 prodotti hanno tasso di riscatto 34% superiore.',
        ctaLabel: 'Vedi insights',
        onCtaClick: () => navigate('/insights')
      }, {
        id: '2',
        title: 'Crescita prodotti anti-age naturali',
        type: 'Trend Categoria',
        diamondRange: '+21% vs mese scorso',
        reason: 'I premi legati a prodotti anti-age naturali sono aumentati del 21% rispetto al mese scorso. Particolare interesse per attivi concentrati e formule personalizzabili nella fascia 35-50 anni.',
        ctaLabel: 'Vedi insights',
        onCtaClick: () => navigate('/insights')
      }, {
        id: '3',
        title: 'Fascia punti ottimale: 40-60',
        type: 'Performance',
        diamondRange: '40-60 punti',
        reason: 'I premi nella fascia 40-60 punti hanno il miglior tasso di conversione (18% più alto della media). Bilanciamento ideale tra valore percepito e accessibilità per gli utenti.',
        ctaLabel: 'Vedi insights',
        onCtaClick: () => navigate('/insights')
      }, {
        id: '4',
        title: 'Prodotti sostenibili in crescita',
        type: 'Trend Sostenibilità',
        diamondRange: '+25% riscatti',
        reason: 'Cosmetici solidi zero-waste e prodotti eco-friendly mostrano crescita del 25% nei riscatti. Gli utenti attenti alla sostenibilità hanno retention 30% più alta.',
        ctaLabel: 'Vedi insights',
        onCtaClick: () => navigate('/insights')
      }]} lastUpdated="Ultimi 30 giorni" data-id="element-1608" />
      </AISidebar>

      {/* Toast Notification */}
      {toast.show && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in" data-id="element-1609">
          {toast.message}
        </div>}
    </div>;
};
export default RewardCreate;